import { App, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import {
  TASK_PRIORITY_LABELS,
  TASK_STATUS,
  TASK_STATUS_LABELS,
  taskPriorityTagClass,
  taskStatusTagClass,
} from '@/constants/task';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import * as tasksApi from '@/services/api/tasks';
import { useAuthStore } from '@/stores/auth.store';
import type { ProjectDetail } from '@/types/project';
import type { TaskDetail } from '@/types/task';
import { formatDateTime } from '@/utils/format-date';

export function EmployeeTaskDetailMobilePage() {
  const { taskId } = useParams<{ taskId: string }>();
  const { message } = App.useApp();
  const user = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [project, setProject] = useState<ProjectDetail | null>(null);

  const load = useCallback(async () => {
    if (!taskId) {
      return;
    }
    setLoading(true);
    try {
      const detail = await tasksApi.fetchTask(taskId);
      setTask(detail);

      try {
        const projectDetail = await projectsApi.fetchProject(detail.projectId);
        setProject(projectDetail);
      } catch {
        setProject(null);
      }
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载任务详情失败';
      message.error(text);
      setTask(null);
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [message, taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isOwner = Boolean(user && task && task.ownerId === user.id);
  const canStart = isOwner && task?.status === TASK_STATUS.PENDING;
  const canComplete = isOwner && task?.status === TASK_STATUS.IN_PROGRESS;
  const showActions = canStart || canComplete;

  const handleStart = async () => {
    if (!taskId || !canStart) {
      return;
    }
    setSubmitting(true);
    try {
      const updated = await tasksApi.startTask(taskId);
      setTask(updated);
      message.success('任务已开始');
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '开始任务失败';
      message.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!taskId || !canComplete) {
      return;
    }
    setSubmitting(true);
    try {
      const updated = await tasksApi.completeTask(taskId);
      setTask(updated);
      message.success('任务已完成');
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '完成任务失败';
      message.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  if (!taskId) {
    return <MobileEmpty message="无效的任务" />;
  }

  if (loading) {
    return (
      <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (!task) {
    return <MobileEmpty message="任务不存在或无权查看" />;
  }

  return (
    <div className="mobile-detail-page">
      <div className="mobile-card mobile-card--flat">
        <div className="mobile-card__header" style={{ marginBottom: 8 }}>
          <span className="mobile-card__title">{task.title}</span>
          <span className={taskStatusTagClass(task.status)}>{TASK_STATUS_LABELS[task.status]}</span>
        </div>

        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">所属项目</span>
          <span className="mobile-detail-row__value">{task.project?.name ?? '—'}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">项目负责人</span>
          <span className="mobile-detail-row__value">{project?.projectManager?.name ?? '—'}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">任务负责人</span>
          <span className="mobile-detail-row__value">{task.owner?.name ?? '—'}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">截止时间</span>
          <span className="mobile-detail-row__value">{formatDateTime(task.dueAt)}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">优先级</span>
          <span className="mobile-detail-row__value">
            <span className={taskPriorityTagClass(task.priority)}>
              {TASK_PRIORITY_LABELS[task.priority]}
            </span>
          </span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">状态</span>
          <span className="mobile-detail-row__value">
            {TASK_STATUS_LABELS[task.status]}
            {task.overdue ? ' · 已逾期' : ''}
          </span>
        </div>
      </div>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">任务内容</span>
        </div>
        <div className="mobile-card mobile-card--flat">
          <p className="mobile-detail-text">{task.content}</p>
        </div>
      </section>

      {task.completionNote ? (
        <section className="mobile-section">
          <div className="mobile-section__header">
            <span className="mobile-section__title">完成备注</span>
          </div>
          <div className="mobile-card mobile-card--flat">
            <p className="mobile-detail-text">{task.completionNote}</p>
          </div>
        </section>
      ) : null}

      {showActions ? (
        <footer className="mobile-detail-footer">
          <div className="mobile-detail-footer__actions">
            {canStart ? (
              <button
                type="button"
                className="mobile-btn mobile-btn--primary mobile-btn--block"
                disabled={submitting}
                onClick={() => void handleStart()}
              >
                {submitting ? '处理中…' : '开始任务'}
              </button>
            ) : null}
            {canComplete ? (
              <button
                type="button"
                className="mobile-btn mobile-btn--primary mobile-btn--block"
                disabled={submitting}
                onClick={() => void handleComplete()}
              >
                {submitting ? '处理中…' : '完成任务'}
              </button>
            ) : null}
          </div>
        </footer>
      ) : null}
    </div>
  );
}
